import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllPrices, getAllYards } from '../services/api';
import { DEMO, useDemo } from './DemoModal';
import { INVENTORY_LOW, MATERIAL_NAME, MATERIAL_UNIT, type Yard, type YardMaterialPrice } from '../types';
import { invClass, money, num, stalenessClass } from '../utils/format';
import { useTableSort } from '../hooks/useTableSort';
import InfoTip from './InfoTip';
import { TIPS } from '../tour/copy';

// One row per yard that stocks the selected material. Single price column:
// single-click a cell to edit (batched, turns blue); double-click a row to
// select that yard for the map. Staleness coloring + a 48h filter toggle.
export default function YardPricesGrid({
  material,
  selectedYardId,
  onSelectYard,
}: {
  material: string;
  selectedYardId: string | null;
  onSelectYard: (yardId: string) => void;
}) {
  const { showDemo } = useDemo();
  const yardsQ = useQuery({ queryKey: ['yards-all'], queryFn: getAllYards });
  const pricesQ = useQuery({ queryKey: ['prices-all'], queryFn: getAllPrices });

  const [pending, setPending] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string | null>(null);

  const yards: Yard[] = yardsQ.data?.yards ?? [];
  const prices: YardMaterialPrice[] = pricesQ.data?.prices ?? [];
  const priceMap = useMemo(() => {
    const m = new Map<string, YardMaterialPrice>();
    for (const p of prices) m.set(`${p.yard_id}::${p.material}`, p);
    return m;
  }, [prices]);

  const rows = useMemo(
    () => yards.filter((y) => (y as any)[material] === true),
    [yards, material]
  );

  const { onSort, sortRows, indicator } = useTableSort<Yard>();
  const invOf = (y: Yard) => priceMap.get(`${y.yard_id}::${material}`)?.inventory ?? 0;
  const priceOf = (y: Yard) => priceMap.get(`${y.yard_id}::${material}`)?.price ?? null;
  const displayRows = sortRows(rows, {
    yard: (y) => y.name,
    price: priceOf,
    inv: invOf,
  });

  const pendingCount = Object.keys(pending).length;

  const submit = () => {
    if (pendingCount) showDemo(DEMO.submitPrices);
  };

  const unit = MATERIAL_UNIT[material] ?? 'unit';

  return (
    <div className="card home-module" data-tour="yard-prices">
      <div className="home-module-header">
        <span>
          {MATERIAL_NAME[material]} prices <span className="muted">($/{unit})</span>
          <InfoTip content={TIPS.yardPriceHeader} />
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {pendingCount > 0 && (
            <button className="btn btn-primary btn-sm" onClick={submit}>
              {`Submit ${pendingCount} update${pendingCount > 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
      <div className="home-module-body">
        <table className="origins-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => onSort('yard')}>
                Yard{indicator('yard')}
              </th>
              <th className="sortable" style={{ textAlign: 'right' }} onClick={() => onSort('price')}>
                Price{indicator('price')}
                <InfoTip content={TIPS.colPrice} />
              </th>
              <th className="sortable" style={{ textAlign: 'right' }} onClick={() => onSort('inv')}>
                INV{indicator('inv')}
                <InfoTip content={TIPS.colInv} />
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((y) => {
              const key = `${y.yard_id}::${material}`;
              const pr = priceMap.get(key);
              const isPending = key in pending;
              const stale = stalenessClass(pr?.updated_at);
              const display = isPending ? pending[key] : pr?.price != null ? String(pr.price) : '';
              const isSelected = selectedYardId === y.yard_id;
              return (
                <tr key={y.yard_id} className={isSelected ? 'row-selected' : ''}>
                  <td
                    className="origins-cell label"
                    onDoubleClick={() => onSelectYard(y.yard_id)}
                    title="Double-click to show this yard on the map"
                  >
                    {y.name}
                  </td>
                  <td
                    className={`origins-cell ${isPending ? 'pending' : ''} ${stale} ${
                      isSelected ? 'selected-cell' : ''
                    }`}
                    onClick={() => setEditing(key)}
                    onDoubleClick={() => onSelectYard(y.yard_id)}
                  >
                    {editing === key ? (
                      <input
                        autoFocus
                        className="origins-cell-input"
                        defaultValue={display}
                        onBlur={(e) => {
                          setPending((p) => ({ ...p, [key]: e.target.value }));
                          setEditing(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          if (e.key === 'Escape') setEditing(null);
                        }}
                      />
                    ) : (
                      <div className="origins-cell-value">
                        {isPending
                          ? display === ''
                            ? '—'
                            : money(Number(display))
                          : money(pr?.price ?? null)}
                      </div>
                    )}
                  </td>
                  <td className={`origins-cell ${invClass(pr?.inventory ?? 0, INVENTORY_LOW)}`}>
                    <div className="origins-cell-value" style={{ whiteSpace: 'nowrap' }}>
                      {num(pr?.inventory ?? 0)} {unit}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td className="origins-cell label muted" colSpan={3}>
                  No yards stock {MATERIAL_NAME[material]}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
